import { Button } from "@/components/ui/button";
import { ErrorText } from "@/components/ui/error";
import { Input } from "@/components/ui/input";
import { loginPath, VITE_API_URL } from "@/constants";
import { useAuth } from "@/providers/auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/auth/verify")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authorize } = useAuth();
  const [isSubmitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [disabled, setDisabled] = useState(false);
  const inputs = () =>
    Array.from(
      document.querySelectorAll("[data-element=code]"),
    ) as HTMLInputElement[];

  const [value, setValue] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await authorize(value);
    } catch (e) {
      if (e === "expired") {
        navigate({ to: loginPath });
      } else {
        setErr(t("auth.verify.invalidPin"));
      }
    }
    setSubmitting(false);
  };

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="w-full flex flex-col space-y-3">
        <div className="text-center"></div>

        <div className="mx-auto w-full max-w-80 relative bottom-8 flex flex-col justify-center gap-6">
          <div className="flex w-full flex-col items-center gap-1">
            <h1 className="text-center text-xl font-semibold tracking-tight text-primary">
              {t("auth.verify.title")}
            </h1>
            <p className="text-center text-sm font-medium text-gray-600">
              {t("auth.verify.subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {err !== "" && (
              <ErrorText className="w-full text-center">{err}</ErrorText>
            )}
            <Input type="hidden" name="code" value={value} />
            <div className="flex space-x-4">
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <Input
                  key={index}
                  className="w-10 h-10 bg-white rounded-sm text-center"
                  data-element="code"
                  maxLength={1}
                  inputMode="numeric"
                  autoFocus={index === 0}
                  disabled={disabled}
                  type="text"
                  onPaste={(e) => {
                    const code = e.clipboardData?.getData("text/plain")?.trim();
                    if (!code) return;
                    const i = inputs();
                    if (code.length !== i.length) return;
                    i.forEach((item, index) => {
                      item.value = code[index];
                    });
                    e.preventDefault();
                    setValue(code);
                  }}
                  onFocus={(e) => {
                    e.currentTarget.select();
                  }}
                  onKeyDown={(e) => {
                    if (!e.currentTarget.value && e.key === "Backspace") {
                      e.preventDefault();
                      const previous = e.currentTarget.previousSibling;
                      if (previous instanceof HTMLInputElement) {
                        previous.value = "";
                        previous.focus();
                      }
                    }
                  }}
                  onInput={(e) => {
                    if (e.currentTarget.value.match("^\\d+$") === null) {
                      e.currentTarget.value = "";
                      return;
                    }
                    const code = [
                      ...document.querySelectorAll("[data-element=code]"),
                    ]
                      .map((el) => (el as HTMLInputElement).value)
                      .join("");
                    setValue(code);
                    const all = inputs();
                    const index = all.indexOf(e.currentTarget);
                    if (!e.currentTarget.value) {
                      const previous = all[index - 1];
                      if (previous) {
                        previous.focus();
                      }
                      return;
                    }

                    const next = all[index + 1];
                    if (next) {
                      next.focus();
                      next.select();
                      return;
                    }

                    setDisabled(false);
                  }}
                />
              ))}
            </div>

            <Button
              type="submit"
              className="flex h-9 items-center justify-center text-white w-full rounded-lg"
              disabled={isSubmitting || value.length !== 6}
            >
              {isSubmitting ? t("auth.verify.verifying") : t("auth.verify.submit")}
            </Button>
          </form>

          {/* Request New Code. */}
          {/* Email is already in session, so no input it's required. */}
          <div className="w-full">
            <span className="text-sm text-gray-600 text-center font-normal flex justify-center items-center">
              {t("auth.verify.noCode")}&nbsp;
              <ResendForm />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResendForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(30);
  const [isCooldownActive, setIsCooldownActive] = useState(true);

  useEffect(() => {
    let interval: number;

    if (isCooldownActive && cooldownTime > 0) {
      interval = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            setIsCooldownActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isCooldownActive, cooldownTime]);

  const resend = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${VITE_API_URL}/auth/resend`, {
        method: "POST",
        headers: {
          "Content-type": "application/json",
        },
        credentials: "include",
      });

      if (response.status > 499) {
        navigate({ to: loginPath });
      }

      if (response.status > 400) {
        throw new Error("failed to verify token");
      }

      toast.info(t("auth.verify.newCodeSent"));

      setCooldownTime(30);
      setIsCooldownActive(true);
    } catch (e) {
      console.log(e);
    }
    setIsSubmitting(false);
  };

  const isDisabled = isSubmitting || isCooldownActive;

  return (
    <form onSubmit={resend}>
      <Button
        type="submit"
        variant={"link"}
        className="px-0 py-0 hover:no-underline"
        disabled={isDisabled}
      >
        {isSubmitting
          ? t("common.sending")
          : isCooldownActive
            ? t("auth.verify.resendIn", { seconds: cooldownTime })
            : t("auth.verify.resend")}
      </Button>
    </form>
  );
}
